import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  Product,
  ProductQuality,
  Store,
  MerchantProduct,
} from "../../../../types/ProductListing";
import ProductQualityBadge from "../../../shared/ProductQualityBadge";
import ProductVariantRow from "./ProductVariantRow";
import { getMerchantProductDisplayValue } from "../../../../utils/merchantProductUtils";
import { calculateProductTotalStock } from "../../../../utils/stockCalculations";
import {
  getProductDisplayPrice,
  formatPriceRange,
} from "../../../../utils/priceCalculations";
import {
  copyMarketplaceProductLink,
  copyMarketplaceStoreLink,
  openMarketplaceProduct,
  openMarketplaceStore,
  getMarketplaceDisplayInfo,
} from "../../../../../../../util/Marketplace";

interface ProductTableRowProps {
  product: Product;
  stores?: Store[];
  onRowClick?: (product: Product) => void;
  onStockUpdate?: (product: Product) => void;
  onPriceUpdate?: (product: Product) => void;
  onEditProduct?: (product: Product) => void;
  showVariants?: boolean;
  className?: string;
  /**
   * When `true`, the trailing Actions `<td>` (edit / copy / open / more
   * menu) is omitted. Used by the chat product picker. Default `false`.
   */
  hideActions?: boolean;
}

function ProductTableRow({
  product,
  stores = [],
  onRowClick,
  onStockUpdate,
  onPriceUpdate,
  onEditProduct,
  showVariants = false,
  className = "",
  hideActions = false,
}: ProductTableRowProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [variantsOpen, setVariantsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [dropdownOpen]);

  const getStockStatusClass = (stock: number) => {
    if (stock === 0) return "tw-text-red-600 tw-font-bold";
    if (stock <= 10) return "tw-text-amber-600 tw-font-bold";
    return "tw-text-gray-800";
  };

  const getProductQuality = (
    rating: number,
    reviewCount: number
  ): ProductQuality => {
    if (reviewCount === 0) return "incomplete";
    if (rating >= 4.0) return "good";
    if (rating >= 3.0) return "incomplete";
    return "poor";
  };

  const getMarketplaceLogo = (marketplace: string) => {
    const logos: Record<string, string> = {
      shopee: "fab fa-shopify",
      lazada: "fas fa-shopping-bag",
      tokopedia: "fas fa-store",
      tiktok: "fab fa-tiktok",
    };
    return logos[marketplace] || "fas fa-store";
  };

  const getMarketplaceName = (marketplace: string) => {
    const names: Record<string, string> = {
      shopee: "Shopee",
      lazada: "Lazada",
      tokopedia: "Tokopedia",
      tiktok: "TikTok Shop",
    };
    return names[marketplace] || marketplace;
  };

  const getStoreDetails = (accountId: string, marketplace: string) => {
    // Handle cases where accountId is empty or stores array is empty
    if (!accountId || !stores || stores.length === 0) {
      return {
        name: `${getMarketplaceName(marketplace)} Store`,
        tooltip: `View on ${getMarketplaceName(marketplace)}`,
      };
    }

    const store = stores.find((s) => s.id === accountId);

    if (store) {
      return {
        name: store.name,
        tooltip: `View on ${getMarketplaceName(marketplace)}`,
      };
    }

    // Fallback if store not found - show accountId for debugging
    return {
      name: accountId
        ? `Store (${accountId})`
        : `${getMarketplaceName(marketplace)} Store`,
      tooltip: `View on ${getMarketplaceName(marketplace)}`,
    };
  };

  const formatPrice = (price: number, currency: string = "USD") => {
    // Convert cents to dollars for USD
    const displayPrice = currency === "USD" ? price / 100 : price;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: currency === "USD" ? 2 : 0,
    }).format(displayPrice);
  };

  const formatEngagementNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "k";
    }
    return num.toString();
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      return `${diffInDays}d ago`;
    }
  };

  // Removed getResponsiveClass - all columns should always be visible to match original design

  // Calculate active variants for consistent logic
  const activeVariants =
    product.variantCombinations?.filter(
      (variant) => variant.isActive === true
    ) || [];
  const hasActiveVariants = activeVariants.length > 0;
  const toggleExpansion = () => {
    if (hasActiveVariants) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      {/* Main Product Row */}
      <tr
        className={clsx(
          "product-table-row",
          onRowClick && "tw-cursor-pointer hover:tw-bg-gray-50",
          className
        )}
        onClick={() => onRowClick?.(product)}
        data-testid={`product-row-${product.id}`}
      >
        {/* Product column */}
        <td
          className="tw-px-4 tw-py-3 tw-align-top"
          data-testid="product-image-cell"
        >
          <div className="tw-flex tw-items-start tw-gap-3">
            {product.mainImage ? (
              <img
                src={product.mainImage}
                alt={product.title}
                className="tw-h-12 tw-w-12 tw-rounded tw-object-cover tw-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="tw-h-12 tw-w-12 tw-rounded tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                <span className="material-symbols-outlined tw-text-gray-400">
                  image
                </span>
              </div>
            )}
            <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
              <div className="custom-tooltip">
                <p
                  className="tw-font-bold tw-text-gray-800 tw-line-clamp-2 tw-text-sm"
                  data-testid="product-name"
                >
                  {product.title}
                </p>
              </div>
              <p
                className="tw-text-xs tw-text-gray-500"
                data-testid="product-sku"
              >
                {product.sku}
              </p>
              <div className="tw-flex tw-items-center tw-gap-x-3 tw-text-xs tw-text-gray-500 tw-mt-1">
                <div className="custom-tooltip tw-flex tw-items-center tw-gap-1 tw-cursor-default">
                  <span>
                    ❤️
                    {formatEngagementNumber(
                      Math.round(product.ratings.totalRatingCount * 0.15)
                    )}
                  </span>
                  <span className="custom-tooltiptext">Likes (30d)</span>
                </div>
                <div className="custom-tooltip tw-flex tw-items-center tw-gap-1 tw-cursor-default">
                  <span>
                    👁️
                    {formatEngagementNumber(
                      Math.round(product.ratings.totalRatingCount * 3.5)
                    )}
                  </span>
                  <span className="custom-tooltiptext">Views (30d)</span>
                </div>

                <div className="custom-tooltip tw-flex tw-items-center tw-gap-1 tw-cursor-default">
                  <span>
                    ⭐ {product.ratings.avgRating.toFixed(1)}
                    {`(${formatEngagementNumber(
                      product.ratings.totalRatingCount
                    )} reviews)`}
                  </span>
                  <span className="custom-tooltiptext">Rating (30d)</span>
                </div>
              </div>
              {hasActiveVariants && (
                <div className="tw-mt-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setVariantsOpen(!variantsOpen);
                    }}
                    className="tw-text-sm tw-font-medium tw-text-primary hover:tw-text-primary/80"
                  >
                    {variantsOpen
                      ? "Hide Variants"
                      : `View ${activeVariants.length} Variant${
                          activeVariants.length === 1 ? "" : "s"
                        }`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Merchant Product column */}
        <td
          className="tw-px-4 tw-py-3 tw-align-top tw-text-sm"
          data-testid="merchant-product-cell"
        >
          {(() => {
            const { value, isClickable } = getMerchantProductDisplayValue(
              product.merchantProduct,
              { maxLength: 1000 }
            );

            if (isClickable) {
              return (
                <div className="custom-tooltip tw-w-[150px] tw-overflow-hidden tw-line-clamp-3 tw-break-words">
                  <a
                    className="tw-text-blue-600 hover:tw-underline"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      console.log(
                        "Merchant product clicked:",
                        product.merchantProduct
                      );
                    }}
                  >
                    {value}
                  </a>
                  <span className="custom-tooltiptext tw-text-xs">{value}</span>
                </div>
              );
            } else {
              return (
                <div className="tw-w-[150px] tw-overflow-hidden tw-line-clamp-3 tw-break-words">
                  <span className="tw-text-gray-500">{value}</span>
                </div>
              );
            }
          })()}
        </td>

        {/* Store column */}
        <td
          className="tw-px-4 tw-py-3 tw-align-top tw-text-sm"
          data-testid="product-store-cell"
        >
          <div className="tw-flex tw-items-start tw-gap-1.5">
            <div className="custom-tooltip tw-w-full">
              {product.accountId ? (
                <a
                  className="tw-text-blue-600 hover:tw-underline tw-break-words"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const store = stores.find(
                      (s) => s.id === product.accountId
                    );
                    openMarketplaceStore(product.marketplace, product, store);
                  }}
                  data-testid="store-link"
                >
                  {getStoreDetails(product.accountId, product.marketplace).name}
                </a>
              ) : (
                <span className="tw-text-gray-500 tw-break-words">
                  {getStoreDetails(product.accountId, product.marketplace).name}
                </span>
              )}
              <span className="custom-tooltiptext">
                {product.accountId
                  ? `View on ${getMarketplaceName(product.marketplace)}`
                  : "No store linked"}
              </span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const store = stores.find((s) => s.id === product.accountId);
                copyMarketplaceStoreLink(product.marketplace, product, store);
              }}
              className="custom-tooltip tw-shrink-0 tw-text-gray-400 hover:tw-text-gray-700"
              disabled={!product.accountId}
            >
              <span className="material-symbols-outlined tw-text-base">
                content_copy
              </span>
              <span className="custom-tooltiptext">
                {product.accountId ? "Copy Store URL" : "No store to copy"}
              </span>
            </button>
          </div>
        </td>

        {/* Stock column */}
        <td
          className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right"
          data-testid="product-stock-cell"
        >
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
            <span
              className={getStockStatusClass(
                calculateProductTotalStock(product)
              )}
              data-testid="product-stock"
            >
              {calculateProductTotalStock(product).toLocaleString()}
            </span>
            <button disabled={product.marketplace === "tokopedia"}
              onClick={(e) => {
                e.stopPropagation();
                if (product.marketplace !== "tokopedia") onStockUpdate?.(product);
              }}
              className="tw-text-gray-400 hover:tw-text-primary custom-tooltip"
            >
              <span className="material-symbols-outlined tw-text-base filled">
                calculate
              </span>
              <span className="custom-tooltiptext">
                Edit stock by warehouse
              </span>
            </button>
          </div>
        </td>

        {/* Price column */}
        <td
          className="tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-text-right"
          data-testid="product-price-cell"
        >
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
            <div className="tw-flex tw-flex-col tw-text-right">
              {(() => {
                const displayPrice = getProductDisplayPrice(product);
                const priceFormatter = new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: displayPrice.currency,
                  minimumFractionDigits: 0,
                });

                if (
                  displayPrice.hasVariants &&
                  typeof displayPrice.current === "object"
                ) {
                  // Product has variants - show price range
                  return (
                    <span
                      className="tw-font-medium tw-text-gray-800"
                      data-testid="product-price"
                    >
                      {formatPriceRange(displayPrice.current, priceFormatter)}
                    </span>
                  );
                } else {
                  // Single product - show price with original if exists
                  return (
                    <>
                      <span
                        className="tw-font-medium tw-text-gray-800"
                        data-testid="product-price"
                      >
                        {priceFormatter.format(displayPrice.current as number)}
                      </span>
                      {displayPrice.original && (
                        <span className="tw-text-xs tw-text-gray-500 tw-line-through">
                          {priceFormatter.format(displayPrice.original)}
                        </span>
                      )}
                    </>
                  );
                }
              })()}
            </div>
            <button disabled={product.marketplace === "tokopedia"}
              onClick={(e) => {
                e.stopPropagation();
                if (product.marketplace !== "tokopedia") onPriceUpdate?.(product);
              }}
              className="tw-text-gray-400 hover:tw-text-primary custom-tooltip"
              data-testid={`edit-price-button-${product.id}`}
            >
              <span className="material-symbols-outlined tw-text-base filled">
                payments
              </span>
              <span className="custom-tooltiptext">
                Edit product or variant pricing
              </span>
            </button>
          </div>
        </td>

        {/* Sold (30d) column */}
        <td
          className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right tw-text-gray-800 tw-font-medium"
          data-testid="product-sold-cell"
        >
          <div className="custom-tooltip">
            <span>{product.salesMetrics.salesQty30d.toLocaleString()}</span>
            <span className="custom-tooltiptext">
              Total units sold in the past 30 days (all variants combined)
            </span>
          </div>
        </td>

        {/* Last Sync column */}
        <td
          className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top tw-text-sm tw-text-right"
          data-testid="product-updated-cell"
        >
          <div className="custom-tooltip tw-flex tw-items-center tw-justify-end tw-gap-1 tw-cursor-pointer">
            <span
              className={
                product.sync.status === "success"
                  ? "tw-text-green-600"
                  : "tw-text-red-600"
              }
            >
              {product.sync.status === "success" ? "✅" : "❌"}
            </span>
            <span className="tw-text-gray-500 tw-italic tw-text-xs">
              {formatTimeAgo(new Date(product.sync.lastSynced || new Date()))}
            </span>
            <span className="custom-tooltiptext">
              Last sync info. Click to see log.
            </span>
          </div>
        </td>

        {/* Product Quality column */}
        <td
          className="tw-px-4 tw-py-3 tw-whitespace-nowrap tw-align-top"
          data-testid="product-quality-cell"
        >
          <ProductQualityBadge
            quality={getProductQuality(
              product.ratings.avgRating,
              product.ratings.totalRatingCount
            )}
          />
        </td>

        {/* Actions column — gated by `hideActions` prop (picker mode hides it) */}
        {!hideActions && (
        <td
          className="tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium"
          data-testid="product-actions-cell"
        >
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditProduct?.(product);
              }}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-100 custom-tooltip"
              disabled={product.marketplace === "tokopedia"}
              data-testid="action-edit-product"
            >
              <span className="material-symbols-outlined tw-text-xl">edit</span>
              <span className="custom-tooltiptext">Edit Product</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyMarketplaceProductLink(product.marketplace, product);
              }}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-100 custom-tooltip"
            >
              <span className="material-symbols-outlined tw-text-xl">
                content_copy
              </span>
              <span className="custom-tooltiptext">Copy Product Link</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openMarketplaceProduct(product.marketplace, product);
              }}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-100 custom-tooltip"
            >
              <span className="material-symbols-outlined tw-text-xl">
                open_in_new
              </span>
              <span className="custom-tooltiptext">Open in Marketplace</span>
            </button>
            <div className="tw-relative" ref={dropdownRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
                className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-gray-600 hover:tw-bg-gray-100 custom-tooltip"
                data-testid="product-action-menu-button"
              >
                <span className="material-symbols-outlined tw-text-xl">
                  more_vert
                </span>
                <span className="custom-tooltiptext">More Options</span>
              </button>
              {dropdownOpen && (
                <div
                  className="tw-absolute tw-right-0 tw-z-10 tw-mt-1 tw-w-56 tw-origin-top-right tw-rounded-md tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 tw-focus:outline-none"
                  data-testid="product-action-dropdown"
                >
                  <div className="tw-py-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        if (product.marketplace !== "tokopedia") onPriceUpdate?.(product);
                      }}
                      className="tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
                      disabled={product.marketplace === "tokopedia"}
                      data-testid="action-update-price"
                    >
                      <span
                        className="material-symbols-outlined tw-mr-3 tw-text-base"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        payments
                      </span>
                      Update Price
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        if (product.marketplace !== "tokopedia") onStockUpdate?.(product);
                      }}
                      className="tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
                      disabled={product.marketplace === "tokopedia"}
                      data-testid="action-update-stock"
                    >
                      <span
                        className="material-symbols-outlined tw-mr-3 tw-text-base"
                        style={{ fontVariationSettings: "'FILL' 1" }}
                      >
                        calculate
                      </span>
                      Update Stock
                    </button>
                    <hr className="tw-my-1" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        const store = stores.find(
                          (s) => s.id === product.accountId
                        );
                        copyMarketplaceStoreLink(
                          product.marketplace,
                          product,
                          store
                        );
                      }}
                      className="tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
                    >
                      <span className="material-symbols-outlined tw-mr-3 tw-text-base">
                        store
                      </span>
                      Copy Store Link
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                        const store = stores.find(
                          (s) => s.id === product.accountId
                        );
                        openMarketplaceStore(
                          product.marketplace,
                          product,
                          store
                        );
                      }}
                      className="tw-flex tw-w-full tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
                    >
                      <span className="material-symbols-outlined tw-mr-3 tw-text-base">
                        open_in_new
                      </span>
                      Open Store in Marketplace
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </td>
        )}
      </tr>

      {/* Variant Rows */}
      {hasActiveVariants &&
        variantsOpen &&
        activeVariants.map((variant) => (
          <ProductVariantRow
            key={variant.id}
            variant={variant}
            parentProduct={product}
            parentProductId={product.id}
            onRowClick={(variant) => {
              // Handle variant click if needed
              console.log("Variant clicked:", variant);
            }}
            onStockUpdate={product.marketplace === "tokopedia" ? undefined : onStockUpdate}
            onPriceUpdate={product.marketplace === "tokopedia" ? undefined : onPriceUpdate}
            hideActions={hideActions}
          />
        ))}
    </>
  );
}

export default ProductTableRow;
export type { ProductTableRowProps };
